import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Eye, ShieldCheck, Compass, Check, Sparkles, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * /catalog — Sovereign Catalog · 3 primary bundles.
 *
 * Pulls the canonical bundle list from /api/catalog (so price/copy
 * changes don't require a frontend redeploy) and offers direct Stripe
 * checkout per bundle. Every purchase pre-stamps the 60/30/10 covenant
 * split on the transaction.
 */
const ICONS = {
    Eye3D: Eye,
    Sentinel: ShieldCheck,
    Wayfinder: Compass,
};

const ACCENTS = {
    privateer_boarding:      { ring: "hsl(45 85% 65%)",  glow: "hsl(45 85% 60% / 0.15)",  label: "Boarding" },
    sovereign_shield:        { ring: "hsl(151 60% 60%)", glow: "hsl(151 60% 50% / 0.15)", label: "Subscription" },
    website_recommissioning: { ring: "hsl(195 70% 65%)", glow: "hsl(195 70% 55% / 0.15)", label: "Re-Commission" },
};

export default function Catalog() {
    const [bundles, setBundles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");
    const [params] = useSearchParams();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/catalog");
                setBundles(data.bundles || []);
            } catch (e) {
                toast.error(e.response?.data?.detail || "Could not load catalog.");
            } finally { setLoading(false); }
        })();
        if (params.get("cancelled")) toast.message("Checkout cancelled — you can re-arm anytime.");
    }, [params]);

    const checkout = async (bundleId) => {
        setBusy(bundleId);
        try {
            const { data } = await api.post("/catalog/checkout", {
                bundle_id: bundleId,
                origin_url: window.location.origin,
            });
            if (data.url) window.location.href = data.url;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Checkout failed.");
            setBusy("");
        }
    };

    return (
        <div className="min-h-screen" data-testid="catalog-page">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
                <div className="text-center fade-up">
                    <div className="text-[10px] uppercase tracking-[0.4em] text-[hsl(45_85%_70%)]">Sovereign Catalog</div>
                    <h1 className="font-heading text-4xl sm:text-6xl tracking-tighter mt-3">Three Furnace Hits.</h1>
                    <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
                        Each bundle enforces the 60/30/10 covenant — 60% to the closing Commander,
                        20% to Director ops, 10% to the CyberKids Anchor. The LAW.
                    </p>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12 animate-pulse">
                        {[0, 1, 2].map((i) => <div key={i} className="h-[480px] rounded-2xl bg-[hsl(151_55%_13%)]" />)}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
                        {bundles.map((b) => {
                            const Icon = ICONS[b.tool_icon] || Sparkles;
                            const a = ACCENTS[b.id] || { ring: "hsl(40 50% 55%)", glow: "rgba(0,0,0,0.1)", label: "Bundle" };
                            return (
                                <article
                                    key={b.id}
                                    className="rounded-2xl border bg-[hsl(151_55%_8%/0.7)] p-6 sm:p-7 flex flex-col relative overflow-hidden hover:scale-[1.02] transition-transform duration-300"
                                    style={{ borderColor: a.ring + "55" }}
                                    data-testid={`catalog-bundle-${b.id}`}
                                >
                                    {/* Tool icon glow */}
                                    <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full pointer-events-none" style={{ background: a.glow, filter: "blur(40px)" }} />
                                    <div className="relative">
                                        <div className="flex items-center justify-between">
                                            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{a.label}</div>
                                            <div className="w-12 h-12 rounded-md flex items-center justify-center" style={{ background: a.ring + "22", color: a.ring }}>
                                                <Icon size={22} strokeWidth={1.4} />
                                            </div>
                                        </div>
                                        <h2 className="font-heading text-3xl tracking-tighter mt-4">{b.name}</h2>
                                        <p className="text-sm text-muted-foreground mt-1">{b.tagline}</p>
                                        <div className="mt-5 flex items-baseline gap-2">
                                            <span className="font-heading text-5xl" style={{ color: a.ring }}>${Math.round(b.price_usd)}</span>
                                            <span className="text-xs text-muted-foreground uppercase tracking-wider">{b.billing === "monthly" ? "/ month" : "one-time"}</span>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-3">{b.description}</p>
                                        <ul className="mt-5 space-y-2.5">
                                            {b.features.map((f, i) => (
                                                <li key={i} className="flex items-start gap-2 text-sm">
                                                    <Check size={14} className="shrink-0 mt-0.5" style={{ color: a.ring }} />
                                                    <span>{f}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <button
                                            onClick={() => checkout(b.id)}
                                            disabled={busy === b.id}
                                            data-testid={`catalog-checkout-${b.id}`}
                                            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md font-semibold text-sm uppercase tracking-wider transition-all disabled:opacity-50"
                                            style={{ background: a.ring, color: "hsl(40 20% 8%)" }}
                                        >
                                            {busy === b.id ? <><Loader2 size={14} className="animate-spin" /> Routing to Stripe…</> : <>{b.cta} <ArrowRight size={14} /></>}
                                        </button>
                                        <div className="mt-3 text-[10px] uppercase tracking-wider text-muted-foreground text-center">
                                            70% Commander · 20% Director · 10% CyberKids
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}

                <div className="mt-16 rounded-2xl border border-[hsl(40_50%_35%/0.4)] bg-[hsl(151_55%_8%/0.6)] p-6 text-center">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_70%)]">Not sure which hit?</div>
                    <h3 className="font-heading text-2xl mt-2">Run the Hull Scanner first.</h3>
                    <p className="text-sm text-muted-foreground mt-2 max-w-xl mx-auto">
                        Free snippet HUD. Performance + Security live. Intelligence locked behind the $49 Delivery Fee — or upgrade straight into Re-Commissioning.
                    </p>
                    <Link
                        to="/#hull-scanner"
                        data-testid="catalog-link-scanner"
                        className="inline-flex items-center gap-2 mt-4 px-4 py-2.5 rounded-md border border-[hsl(40_40%_30%/0.4)] text-xs uppercase tracking-wider hover:bg-[hsl(151_55%_15%)] transition-colors"
                    >
                        Scan my platform <ArrowRight size={12} />
                    </Link>
                </div>
            </div>
        </div>
    );
}
